import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import ForwardIcon from '@mui/icons-material/Forward';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { api } from '@/api/client';
import { formatDateTime } from '@/utils/format';
import type { Referral } from '@/types/medical';

export function ClinicianReferralsPage() {
  const q = useApiQuery({
    queryKey: ['clinician', 'referrals'],
    queryFn:  () => api.get<Referral[]>('/api/medical/clinician/referrals/'),
  });

  return (
    <>
      <PageHeader title="Referrals" />
      <DataState query={q} empty={{
        title: 'No referrals issued yet',
        description: 'Issue referrals from inside an encounter — the appointment context populates the referral with the right ICD codes.',
      }}>
        {(items) => (
          <Stack spacing={1.5}>
            {items.map((r) => (
              <Card key={r.id}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{
                      width: 40, height: 40, borderRadius: 1.5,
                      bgcolor: 'background.subtle', color: 'primary.main',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <ForwardIcon />
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                          Patient #{r.patient}
                        </Typography>
                        <Chip
                          size="small" variant="outlined"
                          label={r.urgency} sx={{ textTransform: 'capitalize' }}
                          color={r.urgency === 'urgent' ? 'warning' : 'default'}
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        Specialty #{r.target_specialty}
                        {r.icd_code && ` · ICD ${r.icd_code}`}
                      </Typography>
                      {r.reason && (
                        <Typography variant="caption" color="text.secondary"
                                    sx={{ display: 'block', mt: 0.5 }}>
                          {r.reason}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" className="tabular">
                      {formatDateTime(r.issued_at)}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </DataState>
    </>
  );
}
